import React, { createContext, useContext } from 'react';
import { MyMessage } from '../components/MyMessage';
import { centralAPI } from '../services/central-api';
import moment from 'moment';

type AuthContextType = {
    /**
     * 3 possible states
     * - undefined: We can't say yet if theres a valid logged user or not (isLoading = true)
     * - null: There's no valid user logged.
     * - AuthenticatedUser: There's a valid  user logged.
     */
    user?: AuthenticatedUser | null;
    isLoading: boolean;
};

const AuthContext = createContext<AuthContextType>({
    isLoading: true,
});

export async function checkSessionStatus(): Promise<{ type: 'success' | 'error' | 'notAccepted' | 'rotatePassword'; user?: AuthenticatedUser; }> {
    const pathname = window.location.pathname;

    if (pathname.includes('/reset-password/') || pathname == '/public-frequently-asked-questions') {
        return {
            type: 'success'
        };
    }

    try {
        const user = await centralAPI.checkAuthentication();

        if (!user.acceptedTermsOfUse) {
            return {
                type: 'notAccepted',
                user
            };
        }

        if (user.passwordRotationDays > 0 && moment().diff(user.passwordResetDate, 'days') > user.passwordRotationDays) {
            return {
                type: 'rotatePassword',
                user
            };
        }

        return {
            type: 'success',
            user
        };
    } catch (e) {

        // Both path names should not show the invalidAuth URL
        // because it's possible that at this point the user is not even
        // logged in, show always omit the message.
        if (!pathname.includes('/login') && pathname != '/') {
            MyMessage.show({ type: 'invalidAuth' });
        } else {
            return {
                type: 'success'
            };
        }


        return {
            type: 'error'
        };
    }
}

function useAuth() {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error('useAuth must be inside AuthProvider');
    }

    return context;
}


const AuthProvider = (props: { children: React.ReactNode; user?: AuthenticatedUser | null; }): React.ReactElement => {
    return <AuthContext.Provider {...props} value={{
        user: props.user,
        isLoading: props.user === undefined,
    }} />;
};


export { AuthProvider, useAuth };
